import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { useLanguage } from "../shared/context/LanguageContext.tsx"
import article from "../shared/assets/images/artical.png"
import { motion, AnimatePresence } from "framer-motion"
import extraLogo from "../shared/assets/images/extra-logo.svg"
import ArticalFormModal from "../shared/components/ArticalFormModal.tsx"

export default function Artical() {
    const { lang } = useParams<{ lang?: string }>()
    const { translation, setLang } = useLanguage()
    const [isUnlocked, setIsUnlocked] = useState(false)

    useEffect(() => {
        setLang(lang === "ar" ? "ar" : "en")
    }, [lang, setLang])

    useEffect(() => {
        if (typeof window !== "undefined") {
            const unlocked = window.localStorage.getItem("articleUnlocked")
            if (unlocked === "true") {
                setIsUnlocked(true)
            }
        }
    }, [])

    const renderDescriptionContent = (content: string) => {
        const splitIndex = content.indexOf(":")
        if (splitIndex === -1 || splitIndex === content.length - 1) {
            return content
        }

        const label = content.slice(0, splitIndex + 1)
        const rest = content.slice(splitIndex + 1).trimStart()

        return (
            <>
                <span className="font-bold">{label}</span>
                {rest ? ` ${rest}` : ""}
            </>
        )
    }

    return (
        <main>
            {/* Logo */}
            <motion.div className="flex items-center justify-center border-b border-secondary py-4">
                <div className="flex-col flex">
                    <img src={lang === "ar" ? "/logos/logo-ar.svg" : "/logos/logo.svg"} alt="NowPay" className="h-6 w-auto" />
                    <div className="flex gap-5 justify-end">
                        <img src={extraLogo} alt="eXtra" className="w-6 h-auto"/>
                    </div>
                </div>
            </motion.div>
            <div className="mx-auto flex w-full flex-col px-5 md:gap-9 md:py-9 md:px-9 lg:py-8 lg:px-[5.625rem] lg:max-w-[90%] xl:max-w-[60%]">
                <div className="flex flex-col items-center justify-center gap-5 md:gap-6 lg:gap-8 mb-4 sm:mb-0">
                    <div className="text-3xl text-center font-bold md:text-4xl whitespace-pre-line">
                        {translation.articleTitle}
                    </div>
                    <img src={article} alt="article image" className="w-full h-64 md:h-80 object-cover rounded-xl"/>
                </div>
                <div className="relative flex flex-col gap-5 md:gap-6 lg:gap-[1.875rem] px-5 lg:px-0 pb-[1.875rem] ld:pb-0">
                    {translation.articalContent.map((item, index) => {
                        if(isUnlocked || item.id <= 3)
                        return <motion.div 
                            key={index} 
                            className="flex flex-col gap-5 md:gap-6 lg:gap-[1.875rem]"
                            initial={isUnlocked && item.id > 3 ? { opacity: 0, y: 20 } : false}
                            animate={isUnlocked && item.id > 3 ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.5, delay: item.id > 3 ? (item.id - 4) * 0.1 : 0, ease: "easeOut" }}
                        >
                            <div className="flex flex-col gap-2.5">
                                <div className="text-base font-bold md:text-lg lg:text-2xl text-primary whitespace-pre-line">
                                    {item.title}
                                </div>
                                <ul className="flex flex-col">
                                    {item.description.map((description, i) => {
                                        const isBlurred = !isUnlocked && item.id === 3 && i > 2
                                        return <motion.div 
                                            key={i} 
                                            className="contents"
                                            initial={isUnlocked && item.id === 3 && i > 2 ? { opacity: 0, filter: "blur(8px)" } : false}
                                            animate={isUnlocked && item.id === 3 && i > 2 ? { opacity: 1, filter: "blur(0px)" } : {}}
                                            transition={{ duration: 0.6, delay: isUnlocked && item.id === 3 && i > 2 ? 0.3 + (i - 3) * 0.1 : 0, ease: "easeOut" }}
                                        >
                                            {description.type === "bullet" ? (
                                                <li className={`text-base font-normal text-black ms-6 md:ms-7 mb-2 lg:ms-8 list-disc whitespace-pre-line ${isBlurred ? "blur-sm select-none" : ""}`}>
                                                    {renderDescriptionContent(description.content)}
                                                </li>
                                            ) : (
                                                <li className={`text-base mb-2 ${description.type === "bold" ? "text-xl font-bold" : "font-normal"} text-black ${description.type === "numbered" ? "ms-1 md:ms-2 lg:ms-3" : description.type === "dashed" ? "ms-6 md:ms-7 lg:ms-8" : ""} list-none whitespace-pre-line ${isBlurred ? "blur-sm select-none" : ""}`}>
                                                    {renderDescriptionContent(description.content)}
                                                </li>
                                            )}
                                            {item.id === 3 && i === 2 && (
                                                <li className={`list-none ${isUnlocked ? "py-0" : "py-4"}`}>
                                                    <AnimatePresence mode="wait">
                                                        {!isUnlocked && (
                                                            <motion.div 
                                                                className="pointer-events-none relative flex justify-center"
                                                                initial={{ opacity: 1 }}
                                                                exit={{ opacity: 0, transition: { duration: 0.4 } }}
                                                            >
                                                                <div className="pointer-events-auto relative">
                                                                    <div className="pointer-events-none absolute -top-[70px] left-1/2 -translate-x-1/2 w-screen h-10 rounded-[28px] bg-white/60 blur-2xl shadow-[0_25px_60px_rgba(255,255,255,0.5)]" aria-hidden="true" />
                                                                    <div className="pointer-events-none absolute -top-[70px] left-1/2 -translate-x-1/2 w-screen h-10 rounded-[24px] bg-white/50 blur-lg shadow-[0_12px_30px_rgba(255,255,255,0.4)]" aria-hidden="true" />
                                                                    <ArticalFormModal
                                                                        isOpen={!isUnlocked}
                                                                        onSuccess={() => {
                                                                            setIsUnlocked(true)
                                                                            if (typeof window !== "undefined") {
                                                                                window.localStorage.setItem("articleUnlocked", "true")
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </li>
                                            )}
                                        </motion.div>
                    })}
                                </ul>
                            </div>
                        </motion.div>
                    })}
                </div>
                {/* Logo */}
                <div className="flex flex-col items-center justify-center w-full">
                    <motion.div className="flex items-center justify-center w-full border-t border-secondary py-4">
                        <div className="flex-col flex pt-4">
                            <img src={lang === "ar" ? "/logos/logo-ar.svg" : "/logos/logo.svg"} alt="NowPay" className="h-4 w-auto" />
                            <div className="flex gap-5 justify-end">
                                <img src={extraLogo} alt="eXtra" className="w-4 h-auto"/>
                            </div>
                        </div>
                    </motion.div>
                    <div className="text-muted-foreground text-sm text-center">{translation.articleCopyright}</div>
                </div>
            </div>
        </main>
    )
}